import { forwardRef } from 'react';
import { cn } from './cn.js';

/**
 * The standard card.
 *
 * Console expresses structure with elevation: a white surface, 16px radius and a soft
 * two-layer shadow on the grey page ground. No border by default — the shadow does the
 * separating. See docs/DESIGN_SYSTEM.md §5.
 */
export const Panel = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(function Panel(
  { className, ...rest },
  ref,
) {
  return (
    <section ref={ref} className={cn('rounded-lg bg-surface shadow-card', className)} {...rest} />
  );
});

// `title` is omitted from the base because the native HTML attribute is a `string`
// (the tooltip), and here it is the rendered heading — a ReactNode.
export interface PanelHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  /** Small text under the title — a period, a date range. */
  subtitle?: React.ReactNode;
  /** Right-aligned slot — a count, a link, carousel arrows. */
  aside?: React.ReactNode;
}

export const PanelHeader = forwardRef<HTMLDivElement, PanelHeaderProps>(function PanelHeader(
  { title, subtitle, aside, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('flex items-start justify-between gap-3 px-5 pt-5 pb-3', className)}
      {...rest}
    >
      <div className="min-w-0">
        <h2 className="text-[1.125rem] font-bold leading-tight tracking-[-0.014em]">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-[0.8125rem] text-ink-muted">{subtitle}</p> : null}
      </div>
      {aside ? <div className="flex shrink-0 items-center gap-1">{aside}</div> : null}
    </div>
  );
});

/**
 * The ‹ › pair the reference puts on card headers. Purely presentational here —
 * wire to real paging when the underlying data supports it.
 */
export function PanelNav({
  onPrev,
  onNext,
  label,
}: {
  onPrev?: () => void;
  onNext?: () => void;
  label: string;
}) {
  const btn =
    'flex size-7 items-center justify-center rounded-sm text-ink-subtle transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk hover:text-ink disabled:opacity-40';
  return (
    <>
      <button type="button" className={btn} onClick={onPrev} aria-label={`Previous ${label}`}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M10 3.5 5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button type="button" className={btn} onClick={onNext} aria-label={`Next ${label}`}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M6 3.5 10.5 8 6 12.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
}
